import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Department } from "../../master-data/master-data.model";
import {
  DocType,
  DocTypeMap,
  LibraryDirectory,
  LibraryDocument,
  LibraryDocumentCreateCommand,
  LibraryDocumentEditCommand,
  LibraryType,
} from "../library.model";
import { MasterDataService } from "../../master-data/master-data-service";
import { finalize } from "rxjs/operators";
import { ConfigurationService } from "../../configuration/configuration.service";
import { Configuration } from "../../configuration/configuration.model";
import { DepartmentsService } from "../../departments/departments.service";
import { DatabaseService } from "../../shared/database.service";
import { Observable, Subscription } from "rxjs";
import { HttpEventType } from "@angular/common/http";
import { MatSnackBar } from "@angular/material/snack-bar";
import { LibraryService } from "../library.service";

@Component({
    selector: "app-library-document-edit",
    templateUrl: "./library-document-edit.component.html",
    styleUrls: ["./library-document-edit.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LibraryDocumentEditComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private departmentsService = inject(DepartmentsService);
  private libraryService = inject(LibraryService);
  private configService = inject(ConfigurationService);
  private databaseService = inject(DatabaseService);
  private masterDataService = inject(MasterDataService);
  private snackBar = inject(MatSnackBar);
  dialogRef = inject<MatDialogRef<LibraryDocumentEditComponent>>(MatDialogRef);
  data = inject<LibraryDocument>(MAT_DIALOG_DATA);

  LibraryT = LibraryType;

  form = this.formBuilder.group({
    uuid: new FormControl<string | null>(null),
    name: new FormControl<string | null>(null, [Validators.required]),
    directory: [null],
    externalId: new FormControl<string | null>(null),
    departmentsUuids: [],
    type: new FormControl<DocType | null>(null, [Validators.required]),
    created: [null],
    path: [null],
  });

  editMode = false;
  departments$: Observable<Department[] | null>;
  directories$: Observable<LibraryDirectory[] | null>;

  config: Configuration = new Configuration();
  fileName = "";
  formData = new FormData();

  docTypes = DocTypeMap;

  uploadProgress: number;
  uploadOK = false;
  uploadSub: Subscription;

  constructor() {
    if (this.data.libraryType === LibraryType.PUBLIC) {
      this.directories$ = this.libraryService.libraryDirectories$;
    } else {
      this.directories$ = this.libraryService.personalDirectories$;
    }

    this.departments$ = this.departmentsService.departments$;
  }

  public compareFn(c1: LibraryDirectory, c2: LibraryDirectory): boolean {
    return c1 && c2 && c1.uuid === c2.uuid;
  }

  ngOnInit(): void {
    this.loadConfig();

    if (this.data.uuid != null) {
      this.editMode = true;
      this.patchValues();
    }
    if (this.data.libraryType === LibraryType.PERSONAL) {
      this.form.controls.directory.setValidators([Validators.required]);
    }
    this.form.updateValueAndValidity();
  }

  private patchValues() {
    this.form.patchValue({
      uuid: this.data.uuid,
      name: this.data.name,
      type: this.data.type,
      directory: this.data.directory ?? null,
      departmentsUuids: this.data.departments.map((d) => d.uuid),
      externalId: this.data.externalId,
    });
    this.form.controls.type.clearValidators();
  }

  private loadConfig() {
    this.configService.config$.subscribe({
      next: (response) => {
        if (response) {
          this.config.setConfig(new Map(Object.entries(response.configMap)));
        }
      },
      error: () => {
        console.log("Http error");
      },
    });
  }

  onCloseClick(): void {
    this.dialogRef.close(this.uploadOK);
  }

  onSave() {
    if (this.editMode) {
      const cmd: LibraryDocumentEditCommand = {
        uuid: this.form.controls.uuid.value,
        name: this.form.controls.name.value,
        directoryUuid:
          this.form.controls.directory.value != null
            ? (this.form.controls.directory.value as LibraryDirectory).uuid
            : null,
        departmentsUuids: this.form.controls.departmentsUuids.value,
        externalId: this.form.controls.externalId.value,
      };

      this.updateDocumentToApi(cmd);
    } else {
      const cmd: LibraryDocumentCreateCommand = {
        name: this.form.controls.name.value,
        type: this.form.controls.type.value,
        directoryUuid:
          this.form.controls.directory.value != null
            ? (this.form.controls.directory.value as LibraryDirectory).uuid
            : null,
        departmentsUuids: this.form.controls.departmentsUuids.value,
        externalId: this.form.controls.externalId.value,
      };
      this.formData.set(
        "command",
        new Blob([JSON.stringify(cmd)], { type: "application/json" })
      );
      this.saveDocumentToApi(this.formData);
    }
  }

  onFileSelected(event) {
    const file: File = event.target.files[0];

    if (file) {
      this.fileName = file.name;
      this.form.controls.name.patchValue(file.name);
      this.formData.set("file", file);
    }
  }

  saveDocumentToApi(doc: FormData) {
    const upload$ = this.databaseService.addDocumentToLibrary(doc).pipe(
      finalize(() => {
        this.resetUpload();
      })
    );
    this.uploadSub = upload$.subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.uploadProgress = Math.round(100 * (event.loaded / event.total));
        }
        if (event.type === HttpEventType.ResponseHeader) {
          if (event.status === 200 || event.status === 201) {
            this.uploadOK = true;
            this.snackBar.open("SUCCESS", "", {
              duration: 2000,
              panelClass: "app-snackbar-success",
            });
            console.log("Upload OK");
          } else {
            this.snackBar.open("ERROR", "", {
              duration: 2000,
              panelClass: "app-snackbar-alert",
            });
            console.log("Upload FAILED");
          }
        }
      },
      error: () => this.masterDataService.savingErrorMessage(),
    });
  }

  updateDocumentToApi(cmd: LibraryDocumentEditCommand) {
    this.databaseService
      .updateDocumentInLibrary(cmd)
      .pipe(
        finalize(() => {
          this.resetUpload();
        })
      )
      .subscribe({
        next: (response) => {
          if (response.status === 200 || response.status === 201) {
            this.uploadOK = true;
            this.snackBar.open("SUCCESS", "", {
              duration: 2000,
              panelClass: "app-snackbar-success",
            });
            console.log("Document Update save - response", response.body);
          } else {
            this.snackBar.open("ERROR", "", {
              duration: 2000,
              panelClass: "app-snackbar-alert",
            });
            console.error("Document Update Error by API");
          }
        },
        error: () => this.masterDataService.savingErrorMessage(),
      });
  }

  cancelUpload() {
    this.uploadSub.unsubscribe();
    this.resetUpload();
  }

  resetUpload() {
    this.uploadProgress = null;
    this.uploadSub = null;
  }

  onTypeChange() {
    this.fileName = "";
    if (this.formData.get("file") !== null) {
      this.formData.delete("file");
    }
  }
}
